import { getFirstTouch } from "./attribution";
import { getVisitorId, isInternal } from "./visitor";
import { gaEventName, type SubmitStatus } from "./events";
import { safeLocal } from "@/lib/storage";
import { isAppContext } from "@/lib/appContext";

const APP_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
const ACTIVE_KEY = "fs_active_variant";

export interface ActiveVariant {
  experiment_id: string;
  variant: string;
}

/** The one experiment variant (if any) the visitor has been exposed to. */
export function getActiveVariant(): ActiveVariant | null {
  if (isAppContext()) return null;
  const ls = safeLocal();
  if (!ls) return null;
  try {
    const raw = ls.getItem(ACTIVE_KEY);
    return raw ? (JSON.parse(raw) as ActiveVariant) : null;
  } catch {
    return null;
  }
}

export function setActiveVariant(v: ActiveVariant): void {
  if (isAppContext()) return;
  const ls = safeLocal();
  if (!ls) return;
  try {
    ls.setItem(ACTIVE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

/** Merge first-touch context, visitor, active variant, and flags with per-event props. */
export function buildPayload(props: Record<string, unknown> = {}): Record<string, unknown> {
  const ft = getFirstTouch();
  const active = getActiveVariant();
  return {
    form_type: null,
    landing_page: ft?.landing_page ?? null,
    page: typeof window !== "undefined" ? window.location.pathname : null,
    referrer: ft?.referrer ?? null,
    utm_source: ft?.utm_source ?? null,
    utm_medium: ft?.utm_medium ?? null,
    utm_campaign: ft?.utm_campaign ?? null,
    visitor_id: getVisitorId(),
    experiment_id: active?.experiment_id ?? null,
    variant: active?.variant ?? null,
    internal: isInternal(),
    app_version: APP_VERSION,
    ...props,
  };
}

// Datadog RUM is loaded via a deferred dynamic import in the consent loader (src/lib/consent/loader.ts) and must never
// be statically imported here (it would re-bloat the eager bundle). The consent loader
// injects the sink after init(); events fired before then are queued.
export interface RumSink {
  addAction: (name: string, ctx?: object) => void;
  addError: (error: unknown, ctx?: object) => void;
}
let rum: RumSink | null = null;
const preInit: Array<[string, Record<string, unknown>]> = [];
const preInitErrors: Array<[Error, Record<string, unknown>]> = [];
const MAX_QUEUE = 50;
const MAX_ERROR_QUEUE = 20;

export function registerRumSink(sink: RumSink): void {
  if (isAppContext()) {
    preInit.length = 0;
    preInitErrors.length = 0;
    return;
  }
  rum = sink;
  for (const [n, c] of preInit) sink.addAction(n, c);
  preInit.length = 0;
  for (const [e, c] of preInitErrors) sink.addError(e, c);
  preInitErrors.length = 0;
}

/**
 * Report a handled fault to Datadog Error Tracking (never to GA: it is not a
 * product event). Use it for conditions the app survived but someone should
 * look at, so they surface where the triage routine looks. Dev/test: buffered
 * to window.__fsAnalytics as `error:<name>` like track().
 */
export function trackError(error: Error, context: Record<string, unknown> = {}): void {
  if (isAppContext()) return;
  if (!import.meta.env.PROD) {
    if (typeof window !== "undefined") {
      (window.__fsAnalytics ||= []).push({ name: `error:${error.name}`, payload: { message: error.message, ...context } });
    }
    console.debug("[analytics:error]", error, context);
    return;
  }
  if (rum) rum.addError(error, context);
  else if (preInitErrors.length < MAX_ERROR_QUEUE) preInitErrors.push([error, context]);
}

/** Fire an event to both sinks in PROD; buffer to window in dev/test. */
export function track(name: string, props: Record<string, unknown> = {}): void {
  if (isAppContext()) return;
  const payload = buildPayload(props);

  if (!import.meta.env.PROD) {
    if (typeof window !== "undefined") (window.__fsAnalytics ||= []).push({ name, payload });
    console.debug("[analytics]", name, payload);
    return;
  }

  // `status` is read from the untyped props bag; lead_submit callers pass a
  // SubmitStatus. A non-matching value simply yields "lead_submit" (never throws).
  const ga = gaEventName(name, payload.status as SubmitStatus | undefined);
  if (ga && typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", ga, payload);
  }

  if (rum) rum.addAction(name, payload);
  else if (preInit.length < MAX_QUEUE) preInit.push([name, payload]);
}
